import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eye, Link2, Unlink, Search, FileCheck2, RefreshCw } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card } from "../ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { openAuthenticatedPdf } from "../../utils/pdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => (iso ? new Date(String(iso).slice(0, 10)).toLocaleDateString("it-IT") : "—");
const fmtEur = (v) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(v || 0);

const DdtCard = ({ ddt, onPreview, onLink, linking, suggested }) => {
  const blocked = !!ddt.linked_order_number;
  return (
    <div className={`bg-white border rounded-lg p-3 ${suggested ? "border-emerald-300" : "border-stone-200"}`} data-testid={`etiflor-ddt-card-${ddt.numero}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-bold text-stone-900">
            DDT n. <span className="font-mono text-[#8A1C2A]" data-testid={`etiflor-ddt-numero-${ddt.numero}`}>{ddt.numero}</span>
            <span className="text-stone-500 font-normal"> del {fmtDate(ddt.data)}</span>
          </p>
          <p className="text-xs text-stone-700 truncate">{ddt.cliente_ragione_sociale} <span className="text-stone-400">· cod. {ddt.cliente_codice}</span></p>
          <p className="text-[11px] text-stone-500 mt-0.5">
            {ddt.num_righe ?? (ddt.righe || []).length} righe · {ddt.colli || 0} colli · {(ddt.peso || 0).toLocaleString("it-IT")} kg · <strong className="text-stone-800">{fmtEur(ddt.totale_documento)}</strong> IVA incl.
          </p>
          {(ddt.reasons || []).length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {ddt.reasons.map((r, i) => (
                <span key={i} className="text-[10px] bg-emerald-50 text-emerald-800 border border-emerald-200 rounded px-1.5 py-0.5">{r}</span>
              ))}
            </div>
          )}
          {!suggested && ddt.customer_match === null && (
            <p className="text-[11px] text-amber-700 mt-1">⚠️ Cliente del DDT diverso da quello dell'ordine</p>
          )}
          {blocked && <p className="text-[11px] text-red-600 mt-1" data-testid={`etiflor-ddt-blocked-${ddt.numero}`}>Già collegato all'ordine {ddt.linked_order_number}</p>}
        </div>
        <div className="flex gap-1.5 shrink-0">
          <Button size="sm" variant="outline" onClick={() => onPreview(ddt)} className="h-8 border-stone-300" data-testid={`etiflor-ddt-preview-${ddt.numero}`}>
            <Eye className="w-3.5 h-3.5 mr-1" /> Anteprima PDF
          </Button>
          <Button size="sm" onClick={() => onLink(ddt)} disabled={blocked || !!linking} className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`etiflor-ddt-link-${ddt.numero}`}>
            <Link2 className="w-3.5 h-3.5 mr-1" /> Collega e spedisci
          </Button>
        </div>
      </div>
    </div>
  );
};

const LinkedBox = ({ order, onUnlink, unlinking }) => (
  <Card className="bg-emerald-50 border-emerald-300 p-3" data-testid="etiflor-linked-box">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div>
        <p className="text-sm font-bold text-emerald-900 flex items-center gap-1.5">
          <FileCheck2 className="w-4 h-4" /> Collegato al DDT Etiflor n. <span className="font-mono" data-testid="etiflor-linked-numero">{order.etiflor_ddt_numero}</span> del {fmtDate(order.etiflor_ddt_data)}
        </p>
        <p className="text-[11px] text-emerald-800">
          Righe, pesi, prezzi e PDF presi da Etiflor{order.etiflor_linked_by ? ` · collegato da ${order.etiflor_linked_by}` : ""}{order.etiflor_linked_at ? ` il ${new Date(order.etiflor_linked_at).toLocaleString("it-IT")}` : ""}
        </p>
        {order.etiflor_update_notice && (
          <p className="text-[11px] bg-amber-100 border border-amber-300 text-amber-900 rounded px-2 py-1 mt-1.5 flex items-center gap-1" data-testid="etiflor-update-notice">
            <RefreshCw className="w-3 h-3" /> {order.etiflor_update_notice}
          </p>
        )}
      </div>
      <div className="flex gap-1.5">
        <Button size="sm" variant="outline" onClick={() => openAuthenticatedPdf(`/etiflor-ddt/${order.etiflor_ddt_id}/pdf`)} className="h-8 border-emerald-400 text-emerald-800" data-testid="etiflor-linked-open-pdf">
          <Eye className="w-3.5 h-3.5 mr-1" /> Apri PDF DDT
        </Button>
        <Button size="sm" variant="outline" onClick={onUnlink} disabled={unlinking} className="h-8 border-red-300 text-red-600 hover:bg-red-50" data-testid="etiflor-unlink-btn">
          <Unlink className="w-3.5 h-3.5 mr-1" /> {unlinking ? "…" : "Scollega"}
        </Button>
      </div>
    </div>
  </Card>
);

export const EtiflorDdtPanel = ({ order, onLinked }) => {
  const linked = !!order.etiflor_ddt_id;
  const [cands, setCands] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [linking, setLinking] = useState(false);
  const [unlinking, setUnlinking] = useState(false);

  useEffect(() => {
    if (linked) return;
    axios.get(`${API}/etiflor-ddt/candidates`, { params: { order_id: order.id } })
      .then((r) => setCands(r.data?.[order.id] || []))
      .catch(() => setCands([]));
  }, [order.id, linked]);

  useEffect(() => {
    if (!q.trim()) { setResults(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/etiflor-ddt/search`, { params: { q: q.trim(), order_id: order.id } });
        setResults(r.data || []);
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, order.id]);

  const preview = (d) => openAuthenticatedPdf(`/etiflor-ddt/${d.id}/pdf`);

  const doLink = async () => {
    if (!confirm) return;
    setLinking(true);
    try {
      const r = await axios.post(`${API}/orders/${order.id}/link-etiflor-ddt`, { ddt_id: confirm.id });
      toast.success(`DDT ${r.data.numero} collegato: ordine SPEDITO, ${r.data.righe} righe aggiornate, PDF allegato`);
      setConfirm(null);
      onLinked?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel collegamento del DDT");
    } finally { setLinking(false); }
  };

  const unlink = async () => {
    if (!window.confirm(`Scollegare il DDT ${order.etiflor_ddt_numero} dall'ordine ${order.order_number}? Le righe dell'ordine restano come sono; il PDF allegato viene rimosso.`)) return;
    setUnlinking(true);
    try {
      await axios.post(`${API}/orders/${order.id}/unlink-etiflor-ddt`);
      toast.success("DDT scollegato");
      onLinked?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nello scollegamento");
    } finally { setUnlinking(false); }
  };

  if (linked) return <LinkedBox order={order} onUnlink={unlink} unlinking={unlinking} />;

  const alreadyShipped = ["shipped", "delivered", "completed"].includes(order.status);

  return (
    <Card className="bg-gradient-to-r from-emerald-50 to-white border-emerald-300 p-3 space-y-3" data-testid="etiflor-ddt-panel">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-emerald-600 text-white flex items-center justify-center shrink-0">
          <FileCheck2 className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-stone-900">DDT da Etiflor</p>
          <p className="text-xs text-stone-600">
            {cands === null ? "Ricerca dei DDT sincronizzati…"
              : cands.length ? `${cands.length} DDT compatibil${cands.length === 1 ? "e" : "i"} trovat${cands.length === 1 ? "o" : "i"} per questo cliente — collega quello giusto: righe reali, Rif. DDT, ${alreadyShipped ? "PDF allegato" : "stato SPEDITO e PDF allegato"} in un click.`
              : "Nessun DDT compatibile ancora sincronizzato per questo cliente. Cerca per numero qui sotto, oppure attendi la prossima sincronizzazione (ogni 10 min)."}
          </p>
        </div>
      </div>

      {cands && cands.length > 0 && (
        <div className="space-y-2" data-testid="etiflor-ddt-suggestions">
          {cands.map((d) => <DdtCard key={d.id} ddt={d} onPreview={preview} onLink={setConfirm} linking={linking} suggested />)}
        </div>
      )}

      <div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca un altro DDT per numero o ragione sociale…" className="pl-8 h-9 bg-white border-emerald-200 text-sm" data-testid="etiflor-ddt-search-input" />
        </div>
        {results !== null && (
          <div className="mt-2 space-y-2" data-testid="etiflor-ddt-search-results">
            {results.length === 0
              ? <p className="text-xs text-stone-500 px-1" data-testid="etiflor-ddt-search-empty">Nessun DDT sincronizzato corrisponde a “{q}”.</p>
              : results.map((d) => <DdtCard key={d.id} ddt={d} onPreview={preview} onLink={setConfirm} linking={linking} />)}
          </div>
        )}
      </div>

      <Dialog open={!!confirm} onOpenChange={(o) => !o && !linking && setConfirm(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="etiflor-link-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Link2 className="w-5 h-5 text-emerald-600" /> Collega DDT {confirm?.numero}</DialogTitle>
          </DialogHeader>
          {confirm && (
            <div className="text-sm space-y-2">
              <p>Collegare il <strong>DDT n. {confirm.numero} del {fmtDate(confirm.data)}</strong> ({confirm.cliente_ragione_sociale}) all'ordine <strong className="font-mono">{order.order_number}</strong>?</p>
              <ul className="text-xs text-stone-700 list-disc pl-5 space-y-1">
                <li>Le righe dell'ordine vengono <strong>sostituite</strong> con le {confirm.num_righe ?? (confirm.righe || []).length} righe del DDT (pesi, prezzi e sconti reali) — totale {fmtEur(confirm.totale_documento)} IVA incl.</li>
                <li>Rif. DDT = <strong>{confirm.numero}</strong>, data spedizione = {fmtDate(confirm.data)}, colli = {confirm.colli || 0}</li>
                {!alreadyShipped && <li>Stato ordine → <strong>SPEDITO</strong> (viene creato il documento di vendita)</li>}
                <li>Il PDF del DDT, identico a quello di Etiflor, viene allegato all'ordine</li>
              </ul>
              {confirm.customer_match === null && !confirm.reasons && (
                <p className="text-xs bg-amber-50 border border-amber-300 text-amber-900 rounded px-2 py-1.5">⚠️ Attenzione: il cliente del DDT non coincide con quello dell'ordine.</p>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirm(null)} disabled={linking} data-testid="etiflor-link-cancel">Annulla</Button>
            <Button onClick={doLink} disabled={linking} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="etiflor-link-confirm">
              {linking ? "Collegamento…" : "Conferma e collega"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default EtiflorDdtPanel;
